/**
 * New Home product catalog
 *
 * Declarative description of the three platform sections of the new home
 * (Figma `1601:13672` — "cards"). Copy is stored as i18n keys, icons are the
 * 36×36 badges exported from Figma and URLs/clients are reused from the shared
 * navigation constants — no new contract is introduced here.
 *
 * @module home-v2/constants/products
 * @category Constants
 */

import { PDT_LINKS, PORTAL_ID_LINKS } from '@/features/home-v2/constants/links';
import type { HomeProduct } from '@/features/home-v2/types/products';

import ManageSearchBadge from '@/assets/icons/home-v2/badge-manage-search.svg?react';
import ListAltBadge from '@/assets/icons/home-v2/badge-list-alt.svg?react';
import FramePersonBadge from '@/assets/icons/home-v2/badge-frame-person.svg?react';
import DevicesBadge from '@/assets/icons/home-v2/badge-devices.svg?react';
import IdCardBadge from '@/assets/icons/home-v2/badge-id-card.svg?react';
import DescriptionBadge from '@/assets/icons/home-v2/badge-description.svg?react';
import LockBadge from '@/assets/icons/home-v2/badge-lock.svg?react';
import PersonDisabledBadge from '@/assets/icons/home-v2/badge-person-disabled.svg?react';
import ArrowSplitDisabledBadge from '@/assets/icons/home-v2/badge-arrow-split-disabled.svg?react';
import QuickReferenceAllDisabledBadge from '@/assets/icons/home-v2/badge-quick-reference-all-disabled.svg?react';
import MonitoringDisabledBadge from '@/assets/icons/home-v2/badge-monitoring-disabled.svg?react';

/** Size of the icon badge in every product row (Figma `icon product`). */
export const PRODUCT_ICON_SIZE = 36;

/** Plataforma Data Trust — Figma `1601:13673`. */
export const PDT_PRODUCTS: readonly HomeProduct[] = [
  {
    key: 'individual-consultation',
    titleKey: 'Individual consultation',
    descriptionKey: 'Perform queries in PDT modules',
    Icon: ManageSearchBadge,
    url: PDT_LINKS.CONSULTA_INDIVIDUAL,
    platform: 'pdt',
  },
  {
    key: 'transaction-history',
    titleKey: 'Transaction history',
    descriptionKey: 'Access the list of the latest transactions',
    Icon: ListAltBadge,
    url: PDT_LINKS.HISTORICO_CONSULTA_INDIVIDUAL,
    platform: 'pdt',
  },
  {
    key: 'background-check',
    titleKey: 'Background Check',
    descriptionKey: 'Access the list of queries',
    Icon: FramePersonBadge,
    url: PDT_LINKS.BACKGROUND_CHECK,
    platform: 'pdt',
  },
] as const;

/** Portal de Identidades Digitais — Figma `1601:13929`. */
export const PORTAL_ID_PRODUCTS: readonly HomeProduct[] = [
  {
    key: 'device-intelligence',
    titleKey: 'Device intelligence',
    descriptionKey: 'Behavior and context analysis',
    Icon: DevicesBadge,
    url: PORTAL_ID_LINKS.CONTEXTUAL_DASHBOARD,
    platform: 'portalId',
  },
  {
    key: 'facial-biometrics',
    titleKey: 'Facial Biometrics',
    descriptionKey: 'Liveness, facial authentication and facial 1:1',
    Icon: IdCardBadge,
    url: PORTAL_ID_LINKS.BIOMETRY_LIVENESS_DASHBOARD,
    platform: 'portalId',
  },
  {
    key: 'documents-verification',
    titleKey: 'Documents Verification',
    descriptionKey: 'Document information and data',
    Icon: DescriptionBadge,
    url: PORTAL_ID_LINKS.DOCUMENTS_TRANSACTIONS,
    platform: 'portalId',
  },
  {
    key: 'mfa',
    titleKey: 'MFA',
    descriptionKey: 'Security layer for transactions',
    Icon: LockBadge,
    url: PORTAL_ID_LINKS.MFA_DASHBOARD,
    platform: 'portalId',
  },
] as const;

/** Plataforma idwall — Figma `1601:14101`, all products "EM BREVE". */
export const IDWALL_PRODUCTS: readonly HomeProduct[] = [
  {
    key: 'profiles',
    titleKey: 'Profiles',
    descriptionKey: 'Perform queries, create and edit profiles',
    Icon: PersonDisabledBadge,
    disabled: true,
  },
  {
    key: 'flows',
    titleKey: 'Flows',
    descriptionKey: 'Create and manage flows and executions',
    Icon: ArrowSplitDisabledBadge,
    disabled: true,
  },
  {
    key: 'pending-reviews',
    titleKey: 'Pending Reviews',
    descriptionKey: 'Access the list and resolve pending items',
    Icon: QuickReferenceAllDisabledBadge,
    disabled: true,
  },
  {
    key: 'data-panel',
    titleKey: 'Data Panel',
    descriptionKey: 'View the platform data panel',
    Icon: MonitoringDisabledBadge,
    disabled: true,
  },
] as const;
